import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Editor } from "primereact/editor";
import { InputText } from "primereact/inputtext";
import {
    DEFAULT_EMAIL_SIGNATURE,
    DEFAULT_EMAIL_SIGNATURE_IMAGE_HEIGHT,
    DEFAULT_EMAIL_SIGNATURE_IMAGE_WIDTH,
    applySignatureImageDimensions,
    extractSignatureFromEmailBody,
    getStoredEmailSignatureSettings,
    normalizeSignatureMarkup,
    replaceSignatureImageSource,
    resetStoredEmailSignatureSettings,
    saveStoredEmailSignatureSettings,
} from "../../services/emailSignature";

const MyMails = () => {
    const { instance, accounts } = useMsal();
    const initialSignatureSettings = getStoredEmailSignatureSettings();
    const [emails, setEmails] = useState([]);
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [signature, setSignature] = useState(initialSignatureSettings.signature);
    const [signatureEditorMode, setSignatureEditorMode] = useState("visual");
    const [signatureImageWidth, setSignatureImageWidth] = useState(initialSignatureSettings.width);
    const [signatureImageHeight, setSignatureImageHeight] = useState(initialSignatureSettings.height);
    const [to, setTo] = useState("");
    const [status, setStatus] = useState("");
    const [statusType, setStatusType] = useState("info");
    const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
    const [replyContext, setReplyContext] = useState(null);
    const [includeSignature, setIncludeSignature] = useState(true);
    const [loading, setLoading] = useState(false);
    const signatureImageInputRef = useRef(null);

    const signaturePreviewHtml = /<[^>]+>/.test(signature || "")
        ? signature
        : (signature || DEFAULT_EMAIL_SIGNATURE).replace(/\n/g, "<br/>");

    const signaturePreviewMarkup = useMemo(() => {
        if (!signaturePreviewHtml) {
            return "";
        }

        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(signaturePreviewHtml, "text/html");
            const root = doc.body;

            root.querySelectorAll("img").forEach((img) => {
                img.style.display = "block";
                img.style.maxWidth = "100%";
                img.style.marginBottom = "0";
                img.style.verticalAlign = "bottom";
            });

            if (root.lastElementChild) {
                root.lastElementChild.style.marginBottom = "0";
            }

            return root.innerHTML;
        } catch (error) {
            return signaturePreviewHtml;
        }
    }, [signaturePreviewHtml]);

    const hasWebmailHostedSignatureImage = /<img[^>]+src=["']https:\/\/(?:mail\.google\.com|outlook\.office\.com|outlook\.office365\.com|attachments\.office\.net)/i.test(signature || "");

    const showStatus = (message, type = "info") => {
        setStatus(message);
        setStatusType(type);
    };

    const getReplyAddress = (email) => {
        const replyToAddress = email?.replyTo?.[0]?.emailAddress?.address;
        const fromAddress = email?.from?.emailAddress?.address;
        return (replyToAddress || fromAddress || "").trim();
    };

    const buildReplySubject = (incomingSubject) => {
        const cleanSubject = (incomingSubject || "").trim();
        if (!cleanSubject) {
            return "Re:";
        }

        return /^re\s*:/i.test(cleanSubject) ? cleanSubject : `Re: ${cleanSubject}`;
    };

    const startReply = (email) => {
        const replyAddress = getReplyAddress(email);
        if (!replyAddress) {
            showStatus("Cannot reply because sender email address is unavailable.", "warn");
            return;
        }

        setTo(replyAddress);
        setSubject(buildReplySubject(email?.subject));
        setReplyContext({
            fromName: email?.from?.emailAddress?.name || "Sender",
            fromAddress: replyAddress,
            subject: email?.subject || "",
        });
        showStatus("Reply mode enabled. Write your message and click Send Reply.", "info");
    };

    const cancelReply = () => {
        setReplyContext(null);
        showStatus("Reply mode canceled.", "info");
    };

    const getAccessToken = async () => {
        const request = {
            scopes: ["Mail.Read", "Mail.Send"],
            account: accounts[0]
        };

        try {
            const response = await instance.acquireTokenSilent(request);
            return response.accessToken;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                return instance.acquireTokenPopup(request).then((response) => {
                    return response.accessToken;
                }).catch((popupError) => {
                    showStatus("Error acquiring token: " + popupError.message, "error");
                    return null;
                });
            }

            showStatus("Error acquiring token: " + error.message, "error");
            return null;
        }
    };

    const fetchEmails = async () => {
        if (accounts.length === 0) {
            showStatus("Please sign in with Microsoft first.", "warn");
            return;
        }

        setLoading(true);
        const token = await getAccessToken();
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const response = await fetch("https://graph.microsoft.com/v1.0/me/mailFolders/Inbox/messages?$top=20&$select=id,subject,from,replyTo,receivedDateTime,bodyPreview,isRead", {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setEmails(data.value);
                showStatus("Emails fetched successfully.", "success");
            } else {
                const errorData = await response.json();
                showStatus("Failed to fetch emails: " + (errorData.error?.message || response.statusText), "error");
            }
        } catch (error) {
            showStatus("Error fetching emails: " + error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    const importSignature = async () => {
        if (accounts.length === 0) {
            showStatus("Please sign in with Microsoft first to import a signature.", "warn");
            return;
        }

        setLoading(true);
        const token = await getAccessToken();
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const response = await fetch("https://graph.microsoft.com/v1.0/me/mailFolders/SentItems/messages?$top=1&$select=body", {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.value && data.value.length > 0) {
                    const extractedSignature = extractSignatureFromEmailBody(data.value[0].body.content);

                    if (!extractedSignature) {
                        showStatus("No reliable signature block was found in the latest sent email. Please paste it manually.", "warn");
                        return;
                    }

                    setSignature(extractedSignature);
                    showStatus("Signature imported from your latest sent email. Review it, then click Save Signature.", "success");
                } else {
                    showStatus("No sent emails were found to import a signature from.", "warn");
                }
            } else {
                const errorData = await response.json();
                showStatus("Failed to fetch sent items: " + (errorData.error?.message || response.statusText), "error");
            }
        } catch (error) {
            showStatus("Error importing signature: " + error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleApplyImageSize = () => {
        if (!/<img[^>]*>/i.test(signature || "")) {
            showStatus("No signature image was found to resize.", "warn");
            return;
        }

        const resizedSignature = applySignatureImageDimensions(
            signature,
            signatureImageWidth,
            signatureImageHeight,
        );

        setSignature(resizedSignature);
        showStatus("Signature image size updated in the editor. Click Save Signature to persist it.", "success");
    };

    const handleSignatureImageUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        try {
            const dataUrl = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(String(reader.result || ""));
                reader.onerror = () => reject(new Error("Image upload failed"));
                reader.readAsDataURL(file);
            });

            if (!dataUrl.startsWith("data:image/")) {
                throw new Error("Only image files are supported.");
            }

            setSignature((currentSignature) => replaceSignatureImageSource(
                currentSignature || DEFAULT_EMAIL_SIGNATURE,
                dataUrl,
                signatureImageWidth,
                signatureImageHeight,
            ));
            showStatus("Signature image added. Click Save Signature to make CRM use it.", "success");
        } catch (error) {
            showStatus(error.message || "Unable to add the signature image.", "error");
        } finally {
            event.target.value = "";
        }
    };

    const saveSignature = () => {
        const savedSettings = saveStoredEmailSignatureSettings({
            signature,
            width: signatureImageWidth,
            height: signatureImageHeight,
        });

        setSignature(savedSettings.signature);
        setSignatureImageWidth(savedSettings.width);
        setSignatureImageHeight(savedSettings.height);
        showStatus("Signature saved. CRM emails will now use this saved signature.", "success");
    };

    const resetSignature = () => {
        const resetSettings = resetStoredEmailSignatureSettings();
        setSignature(resetSettings.signature);
        setSignatureImageWidth(resetSettings.width);
        setSignatureImageHeight(resetSettings.height);
        showStatus("The default signature has been restored.", "info");
    };

    const sendEmail = async (e) => {
        e.preventDefault();
        if (accounts.length === 0) {
            showStatus("Please sign in with Microsoft first.", "warn");
            return;
        }

        setLoading(true);
        const token = await getAccessToken();
        if (!token) {
            setLoading(false);
            return;
        }

        const normalizedSignature = normalizeSignatureMarkup(signature || DEFAULT_EMAIL_SIGNATURE);
        const finalSignature = /<[^>]+>/.test(normalizedSignature)
            ? normalizedSignature
            : normalizedSignature.replace(/\n/g, "<br/>");

        const emailContent = includeSignature
            ? `${body.replace(/\n/g, "<br/>")}<br/><br/>${finalSignature}`
            : body.replace(/\n/g, "<br/>");

        const emailData = {
            message: {
                subject: subject,
                body: {
                    contentType: "HTML",
                    content: emailContent
                },
                toRecipients: [
                    {
                        emailAddress: {
                            address: to
                        }
                    }
                ]
            },
            saveToSentItems: "true"
        };

        try {
            const response = await fetch("https://graph.microsoft.com/v1.0/me/sendMail", {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(emailData)
            });

            if (response.ok) {
                showStatus("Email sent successfully.", "success");
                setSubject("");
                setBody("");
                setTo("");
                setReplyContext(null);
            } else {
                const errorData = await response.json();
                showStatus("Failed to send email: " + (errorData.error?.message || response.statusText), "error");
            }
        } catch (error) {
            showStatus("Error sending email: " + error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (accounts.length > 0) {
            fetchEmails();
        }
    }, [accounts]);

    return (
        <div style={{ padding: "20px", fontFamily: "Arial, sans-serif", background: "linear-gradient(180deg, #f4f8ff 0%, #eef7f3 100%)", minHeight: "100vh" }}>
            <h2 style={{ marginBottom: "12px", color: "#0f172a" }}>My Mails</h2>

            {status && (
                <div style={{
                    padding: "10px",
                    margin: "10px 0",
                    backgroundColor: statusType === "error" ? "#fff1f2" : statusType === "success" ? "#ecfdf3" : statusType === "warn" ? "#fffbeb" : "#eff6ff",
                    borderLeft: `4px solid ${statusType === "error" ? "#dc2626" : statusType === "success" ? "#16a34a" : statusType === "warn" ? "#d97706" : "#2563eb"}`,
                    borderRadius: "8px",
                    color: "#1f2937"
                }}>
                    {status}
                </div>
            )}

            <div style={{ display: "flex", gap: "20px", marginTop: "20px", alignItems: "flex-start", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: "340px", border: "1px solid #dbe5f0", padding: "16px", borderRadius: "14px", backgroundColor: "#ffffff", boxShadow: "0 12px 28px rgba(15, 23, 42, 0.08)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                        <h3 style={{ margin: 0, color: "#0f172a" }}>Inbox (Last 20)</h3>
                        <Button type="button" label="Refresh" onClick={fetchEmails} disabled={loading} className="p-button-outlined p-button-sm" />
                    </div>

                    {loading && emails.length === 0 ? (
                        <p>Loading emails...</p>
                    ) : (
                        <div style={{ maxHeight: "560px", overflowY: "auto", border: "1px solid #e6edf5", borderRadius: "10px", backgroundColor: "#f8fbff" }}>
                            {emails.length === 0 ? (
                                <p style={{ padding: "12px", margin: 0 }}>No emails found.</p>
                            ) : (
                                emails.map((email) => (
                                    <div key={email.id} style={{ padding: "12px", borderBottom: "1px solid #e6edf5", backgroundColor: email.isRead ? "#ffffff" : "#eef7ff" }}>
                                        <div style={{ fontWeight: "bold", marginBottom: "5px", color: "#0f172a" }}>{email.from?.emailAddress?.name || "Unknown Sender"}</div>
                                        <div style={{ color: "#1d4ed8", marginBottom: "5px" }}>{email.subject}</div>
                                        <div style={{ fontSize: "14px", color: "#475569" }}>{email.bodyPreview}</div>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "8px", gap: "10px", flexWrap: "wrap" }}>
                                            <div style={{ fontSize: "12px", color: "#64748b" }}>
                                                {new Date(email.receivedDateTime).toLocaleString()}
                                            </div>
                                            <Button
                                                type="button"
                                                label="Reply"
                                                icon="pi pi-reply"
                                                className="p-button-text p-button-sm"
                                                onClick={() => startReply(email)}
                                            />
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>

                <div style={{ flex: 1, minWidth: "340px", border: "1px solid #dbe5f0", padding: "16px", borderRadius: "14px", backgroundColor: "#ffffff", boxShadow: "0 12px 28px rgba(15, 23, 42, 0.08)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px", gap: "12px", flexWrap: "wrap" }}>
                        <h3 style={{ margin: 0, color: "#0f172a" }}>Send New Email</h3>
                        <Button
                            type="button"
                            label="Manage Signature"
                            icon="pi pi-pencil"
                            className="p-button-outlined p-button-sm"
                            onClick={() => setIsSignatureDialogOpen(true)}
                        />
                    </div>
                    <form onSubmit={sendEmail}>
                        {replyContext && (
                            <div style={{ marginBottom: "12px", padding: "10px 12px", border: "1px solid #c7d2fe", backgroundColor: "#eef2ff", borderRadius: "8px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                <div style={{ fontSize: "13px", color: "#3730a3" }}>
                                    Replying to {replyContext.fromName} ({replyContext.fromAddress})
                                </div>
                                <Button
                                    type="button"
                                    label="Cancel Reply"
                                    className="p-button-text p-button-sm"
                                    onClick={cancelReply}
                                />
                            </div>
                        )}

                        <div style={{ marginBottom: "15px" }}>
                            <label style={{ display: "block", marginBottom: "5px" }}>To:</label>
                            <InputText
                                type="email"
                                value={to}
                                onChange={(e) => setTo(e.target.value)}
                                required
                                className="w-full"
                                placeholder="recipient@example.com"
                            />
                        </div>
                        <div style={{ marginBottom: "15px" }}>
                            <label style={{ display: "block", marginBottom: "5px" }}>Subject:</label>
                            <InputText
                                type="text"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                required
                                className="w-full"
                                placeholder="Email Subject"
                            />
                        </div>
                        <div style={{ marginBottom: "15px" }}>
                            <label style={{ display: "block", marginBottom: "5px" }}>Message:</label>
                            <textarea
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                required
                                style={{ width: "100%", padding: "8px", height: "150px", boxSizing: "border-box", border: "1px solid #ced4da", borderRadius: "8px" }}
                                placeholder="Type your message here..."
                            />
                        </div>
                        <div style={{ marginBottom: "15px", fontSize: "13px", color: "#666" }}>
                            Saved signature will be appended automatically to test emails.
                        </div>
                        <div style={{ marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" }}>
                            <input
                                id="includeSignature"
                                type="checkbox"
                                checked={includeSignature}
                                onChange={(e) => setIncludeSignature(Boolean(e.target.checked))}
                            />
                            <label htmlFor="includeSignature" style={{ fontSize: "13px", color: "#334155", cursor: "pointer" }}>
                                Include signature
                            </label>
                        </div>
                        <Button
                            type="submit"
                            label={loading ? "Sending..." : (replyContext ? "Send Reply" : "Send Email")}
                            icon="pi pi-send"
                            disabled={loading}
                        />
                    </form>
                </div>
            </div>

            <Dialog
                header="Manage Signature"
                visible={isSignatureDialogOpen}
                onHide={() => setIsSignatureDialogOpen(false)}
                style={{ width: "46vw", maxWidth: "720px" }}
                draggable={false}
                resizable={false}
            >
                <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", marginBottom: "16px", flexWrap: "wrap" }}>
                        <div>
                            <label style={{ display: "block", fontSize: "14px", fontWeight: 600, color: "#334e68" }}>Saved Signature</label>
                            <p style={{ margin: "6px 0 0", color: "#52606d", fontSize: "13px" }}>
                                Save one reusable signature here. CRM email compose will use the saved version.
                            </p>
                        </div>
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end" }}>
                            <input
                                ref={signatureImageInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: "none" }}
                                onChange={handleSignatureImageUpload}
                            />
                            <Button type="button" label="Upload Image" icon="pi pi-image" className="p-button-text p-button-sm" onClick={() => signatureImageInputRef.current?.click()} disabled={loading} />
                            <Button type="button" label="Import from Sent" icon="pi pi-download" className="p-button-text p-button-sm" onClick={importSignature} disabled={loading} />
                            <Button type="button" label="Reset" icon="pi pi-refresh" className="p-button-text p-button-sm" onClick={resetSignature} disabled={loading} />
                            <Button type="button" label="Save Signature" icon="pi pi-save" className="p-button-text p-button-sm" onClick={saveSignature} disabled={loading} />
                        </div>
                    </div>

                    <div style={{ display: "inline-flex", gap: "8px", padding: "4px", backgroundColor: "#f0f4f8", borderRadius: "999px", marginBottom: "14px" }}>
                        <Button type="button" label="Visual" className={signatureEditorMode === "visual" ? "p-button-sm" : "p-button-text p-button-sm"} onClick={() => setSignatureEditorMode("visual")} />
                        <Button type="button" label="HTML Source" className={signatureEditorMode === "html" ? "p-button-sm" : "p-button-text p-button-sm"} onClick={() => setSignatureEditorMode("html")} />
                    </div>

                    {signatureEditorMode === "visual" ? (
                        <Editor
                            value={signature}
                            onTextChange={(e) => setSignature(e.htmlValue || "")}
                            style={{ height: "180px", marginBottom: "14px" }}
                        />
                    ) : (
                        <textarea
                            value={signature}
                            onChange={(e) => setSignature(e.target.value)}
                            rows={8}
                            style={{ width: "100%", border: "1px solid #cbd2d9", borderRadius: "8px", padding: "12px", resize: "vertical", fontFamily: "Consolas, monospace", fontSize: "12px", minHeight: "180px", marginBottom: "14px" }}
                            placeholder="Paste raw HTML signature here"
                        />
                    )}

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "12px", alignItems: "end", marginBottom: "14px" }}>
                        <div>
                            <label htmlFor="signatureImageWidth" style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#334e68", marginBottom: "6px" }}>Image width</label>
                            <InputText
                                id="signatureImageWidth"
                                value={signatureImageWidth}
                                onChange={(e) => setSignatureImageWidth(e.target.value.replace(/[^0-9]/g, ""))}
                                placeholder={String(DEFAULT_EMAIL_SIGNATURE_IMAGE_WIDTH)}
                                className="w-full"
                            />
                        </div>
                        <div>
                            <label htmlFor="signatureImageHeight" style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#334e68", marginBottom: "6px" }}>Image height</label>
                            <InputText
                                id="signatureImageHeight"
                                value={signatureImageHeight}
                                onChange={(e) => setSignatureImageHeight(e.target.value.replace(/[^0-9]/g, ""))}
                                placeholder={String(DEFAULT_EMAIL_SIGNATURE_IMAGE_HEIGHT)}
                                className="w-full"
                            />
                        </div>
                        <Button type="button" label="Apply Size" icon="pi pi-arrows-alt" className="p-button-outlined" onClick={handleApplyImageSize} />
                    </div>

                    {hasWebmailHostedSignatureImage && (
                        <div style={{ backgroundColor: "#fff7ed", color: "#9a3412", border: "1px solid #fdba74", borderRadius: "10px", padding: "10px 12px", marginBottom: "14px" }}>
                            The current signature image uses a Gmail or Outlook attachment URL. Upload the image directly so CRM can reuse it reliably.
                        </div>
                    )}

                    <div style={{ border: "1px solid #d9e2ec", borderRadius: "10px", overflow: "hidden" }}>
                        <div style={{ padding: "10px 14px", backgroundColor: "#f8fafc", borderBottom: "1px solid #d9e2ec", fontSize: "13px", fontWeight: 600, color: "#334e68" }}>
                            Signature Preview
                        </div>
                        <div
                            style={{ padding: "16px 16px 0", backgroundColor: "#ffffff", minHeight: "170px", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}
                            dangerouslySetInnerHTML={{ __html: signaturePreviewMarkup }}
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default MyMails;